import React, { useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, AlertTriangle, ChevronDown } from 'lucide-react';
import { MIN_YEAR } from '../../prototypes/svs-year-end-v2/shared/content';
import { clampIncome, INCOME_SLIDER, MAX_YEAR, PAID_STOPS, type Mode } from '../../prototypes/svs-year-end-v2/shared/calc';
import { useSvsEstimate } from '../../prototypes/svs-year-end-v2/shared/useSvsEstimate';
import { dict, formatEuro, type Lang } from '../../prototypes/svs-year-end-v2/flow-short/i18n';
import { Rich } from '../../prototypes/svs-year-end-v2/flow-short/lang';
import { useCountUp, useDebounced } from '../../prototypes/svs-year-end-v2/flow-short/ui';
import './svs-estimate-calculator.css';

// SVS rough calculator for the long SVS guides (DE + EN). Same logic and same
// copy as the approved V2.5 SVS-Kurzinfo calculator
// (src/prototypes/svs-year-end-v2/flow-short/Calculator.tsx) – only the
// bottom sheets are replaced by inline expandables, and the guide gets a
// prominent "rough estimate only" notice plus a link to the Kurzinfo.
// SSR-safe: window/matchMedia only inside effects.

// Guide-only copy (not part of the Kurzinfo dictionary).
const guideCopy = {
  de: {
    noticeTitle: 'Das ist nur eine grobe Schätzung',
    noticeText:
      'Der Rechner vereinfacht stark (Faustregeln, keine Sonderregeln für Neugründer, unterjährigen Start oder Nachzahlungen für Vorjahre). Er ersetzt keine Berechnung mit deinen echten Zahlen.',
    kurzinfoLink: 'So läuft die freiwillige SVS-Vorauszahlung bei PSTax ab →',
    kurzinfoHref: '/de/guides/svs-sozialversicherung/kurz/',
  },
  en: {
    noticeTitle: 'This is only a rough estimate',
    noticeText:
      'The calculator simplifies a lot (rules of thumb, no special rules for new businesses, starting mid-year or back payments for previous years). It doesn’t replace a calculation with your real figures.',
    kurzinfoLink: 'How the voluntary SVS prepayment works at PSTax →',
    kurzinfoHref: '/en/guides/svs-social-insurance/short/',
  },
} as const;

const pct = (v: number, min: number, max: number) => `${((v - min) / (max - min || 1)) * 100}%`;

// Same text styles as the Kurzinfo calculator.
const NUM = 'text-[1.5rem] font-bold leading-tight tabular-nums';
const TITLE = 'text-[15px] font-bold leading-snug text-pstax-navy';
const LABEL = 'text-[13px] font-semibold leading-snug text-pstax-gray';

/** Neutral blue "!" shown only at the income minimum – opens right away, then hover / tap. */
function Tip({ text, aria }: { text: string; aria: string }) {
  const [open, setOpen] = useState(true);
  const id = useId();
  return (
    <span className="relative inline-flex" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        type="button"
        aria-describedby={open ? id : undefined}
        aria-label={aria}
        onClick={() => setOpen((o) => !o)}
        onBlur={() => setOpen(false)}
        className="flex h-6 w-6 items-center justify-center rounded-full text-pstax-link hover:bg-pstax-blue/15 focus-visible:outline-2 focus-visible:outline-pstax-link"
      >
        <AlertCircle size={16} aria-hidden="true" />
      </button>
      {open && (
        <span
          id={id}
          role="tooltip"
          aria-live="polite"
          className="absolute left-0 top-full z-20 mt-1 w-60 max-w-[calc(100vw-4rem)] rounded-lg bg-pstax-navy px-3 py-2 text-[12px] font-medium leading-snug text-white shadow-lg"
        >
          {text}
        </span>
      )}
    </span>
  );
}

/** Inline expandable: button with aria-expanded + ChevronDown, content in normal page flow. */
function Expandable({
  label,
  children,
  className = '',
  labelClassName = TITLE,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
  labelClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <div className={className}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((o) => !o)}
        className="flex min-h-[40px] w-full items-center gap-2 rounded-lg text-left focus-visible:outline-2 focus-visible:outline-pstax-link"
      >
        <span className={`${labelClassName} flex-1`}>{label}</span>
        <ChevronDown
          size={17}
          aria-hidden="true"
          className={`shrink-0 text-pstax-gray transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      <div id={id} hidden={!open} className="pb-2 pt-1">
        {children}
      </div>
    </div>
  );
}

function DetailBody({ lead, body }: { lead?: string; body?: string[] }) {
  return (
    <div className="space-y-3 border-l-2 border-pstax-blue pl-4 text-[15px] leading-relaxed text-pstax-navy/85">
      {lead && <p className="font-medium text-pstax-navy">{lead}</p>}
      {body?.map((p) => <p key={p}>{p}</p>)}
    </div>
  );
}

function Bullets({ items }: { items: string[] }) {
  return (
    <ul className="list-disc space-y-1.5 pl-5 text-[15px] leading-relaxed marker:text-pstax-navy/60">
      {items.map((p) => (
        <li key={p}>{p}</li>
      ))}
    </ul>
  );
}

function WhenBody({ lang }: { lang: Lang }) {
  const w = dict[lang].calc.when;
  return (
    <div className="space-y-5 text-[15px] leading-relaxed text-pstax-navy">
      <div className="space-y-3">
        <p>{w.general}</p>
        <p>
          <Rich text={w.rule} />
        </p>
      </div>
      <div>
        <p className="mb-2 font-bold">{w.yesTitle}</p>
        <p className="mb-1.5">{w.ifLead}</p>
        <Bullets items={w.yes} />
      </div>
      <div>
        <p className="mb-2 font-bold">{w.noTitle}</p>
        <p className="mb-1.5">{w.ifLead}</p>
        <Bullets items={w.no} />
      </div>
    </div>
  );
}

export function SvsEstimateCalculator({ lang }: { lang: 'de' | 'en' }) {
  const s = useSvsEstimate();
  const c = dict[lang].calc;
  const g = guideCopy[lang];
  const euro = (v: number) => formatEuro(v, lang);
  const incomeId = useId();
  const paidId = useId();
  const modeId = useId();
  const headingId = useId();

  // Count-up only when the user hasn't asked for reduced motion (checked after mount – SSR-safe).
  const [anim, setAnim] = useState(false);
  useEffect(() => {
    setAnim(!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
  }, []);
  const shownOpen = useCountUp(s.open, anim);

  const result = c.results[s.level];
  const announced = useDebounced(
    `${c.svsLabel}: ${euro(s.svsYear)}. ${c.resultLabel}: ${euro(s.open)}. ${result.title}.`,
  );
  const w = c.paidWarning;
  const [paidInfoOpen, setPaidInfoOpen] = useState(false);
  const paidInfoId = useId();

  return (
    <div className="my-8">
      <section
        aria-labelledby={headingId}
        className="rounded-2xl border border-pstax-border bg-white p-4 shadow-sm sm:p-6"
      >
        <p className="text-[11px] font-black uppercase tracking-wider text-pstax-blue">{c.kicker}</p>
        <h4 id={headingId} className="mt-1 text-xl font-display font-bold text-pstax-navy">
          {c.heading}
        </h4>

        {/* Required notice (client): rough estimate only */}
        <div className="mt-4 rounded-xl border border-amber-200 border-l-4 border-l-amber-500 bg-amber-50 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle size={22} className="mt-0.5 shrink-0 text-amber-600" aria-hidden="true" />
            <div className="min-w-0">
              <p className="text-base font-bold leading-snug text-pstax-navy">{g.noticeTitle}</p>
              <p className="mt-1 text-[15px] leading-relaxed text-pstax-navy">{g.noticeText}</p>
            </div>
          </div>
          <Expandable label={c.assumptions.title} className="mt-2 border-t border-amber-200 pt-1">
            <DetailBody body={c.assumptions.body} />
          </Expandable>
        </div>

        {/* Berechnungsvariante */}
        <div className="mt-5 flex flex-wrap items-center justify-end gap-x-3 gap-y-2">
          <span id={modeId} className={LABEL}>
            {c.modeLabel}
          </span>
          <div
            role="group"
            aria-labelledby={modeId}
            className="grid shrink-0 grid-cols-2 gap-0.5 rounded-full border border-pstax-border bg-pstax-bg p-0.5"
          >
            {(['revenue', 'profit'] as Mode[]).map((mode) => (
              <button
                key={mode}
                type="button"
                aria-pressed={s.mode === mode}
                onClick={() => s.setMode(mode)}
                className={`min-h-[34px] rounded-full px-3 text-[13px] font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-pstax-link ${
                  s.mode === mode ? 'bg-pstax-navy text-white' : 'text-pstax-navy hover:bg-white'
                }`}
              >
                {c.modes[mode]}
              </button>
            ))}
          </div>
        </div>

        {/* Income */}
        <div className="mt-4">
          <div className="flex items-end justify-between gap-3">
            <div className="flex items-center gap-1">
              <label htmlFor={incomeId} className={LABEL}>
                {c.incomeLabel[s.mode]}
              </label>
              {s.income <= s.incomeRange.min && <Tip key={s.mode} text={c.incomeTip[s.mode]} aria={c.tipAria} />}
            </div>
            <p className={`${NUM} shrink-0 text-right text-pstax-navy`} aria-hidden="true">
              {euro(s.income)}
            </p>
          </div>
          <input
            id={incomeId}
            type="range"
            className="svs-calc-range"
            min={INCOME_SLIDER.min}
            max={INCOME_SLIDER.max}
            step={INCOME_SLIDER.step}
            value={s.income}
            aria-valuetext={euro(s.income)}
            onChange={(e) => s.setIncome(clampIncome(s.mode, Number(e.target.value)))}
            style={{ ['--fill' as string]: pct(s.income, INCOME_SLIDER.min, INCOME_SLIDER.max) } as React.CSSProperties}
          />
        </div>

        {/* Already paid for 2026 */}
        <div className="mt-3">
          <div className="flex items-end justify-between gap-3">
            <div className="flex items-center gap-1">
              <label htmlFor={paidId} className={LABEL}>
                {c.paidLabel}
              </label>
              <button
                type="button"
                aria-expanded={paidInfoOpen}
                aria-controls={paidInfoId}
                aria-label={w.detail.title}
                onClick={() => setPaidInfoOpen((o) => !o)}
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-red-600 hover:bg-red-50 focus-visible:outline-2 focus-visible:outline-pstax-link"
              >
                <AlertCircle size={17} aria-hidden="true" />
              </button>
            </div>
            <p className={`${NUM} shrink-0 text-right text-pstax-navy`} aria-hidden="true">
              {euro(s.paid)}
              {(s.paid === MIN_YEAR || s.paid === MAX_YEAR) && (
                <span className={`${LABEL} block`}>({s.paid === MIN_YEAR ? c.minNote : c.maxNote})</span>
              )}
            </p>
          </div>
          <div id={paidInfoId} hidden={!paidInfoOpen} className="mb-2 mt-2 rounded-xl bg-red-50/60 p-3">
            <p className="mb-2 text-[15px] font-bold text-pstax-navy">{w.detail.title}</p>
            <DetailBody lead={w.text} body={w.detail.body} />
          </div>
          <input
            id={paidId}
            type="range"
            className="svs-calc-range"
            min={0}
            max={s.paidMaxIndex}
            step={1}
            value={s.paidIndex}
            aria-valuetext={euro(PAID_STOPS[s.paidIndex])}
            onChange={(e) => s.setPaidIndex(Number(e.target.value))}
            style={{ ['--fill' as string]: pct(s.paidIndex, 0, s.paidMaxIndex) } as React.CSSProperties}
          />
        </div>

        {/* Result as one calculation: SVS 2026 − bezahlt = offen */}
        <div className="mt-4 rounded-2xl border-2 border-pstax-navy/70 bg-white px-3 py-3 sm:px-4">
          <p className={TITLE}>{c.resultLabel}</p>
          <div
            className="mt-1 flex flex-wrap items-end gap-x-2.5 gap-y-1 tabular-nums text-pstax-navy"
            aria-hidden="true"
          >
            <div>
              <p className={LABEL}>{c.sumTotal}</p>
              <p className={NUM}>{euro(s.svsYear)}</p>
            </div>
            <p className={`${NUM} text-pstax-gray`}>−</p>
            <div>
              <p className={LABEL}>{c.sumPaid}</p>
              <p className={NUM}>{euro(s.paid)}</p>
            </div>
            <p className={`${NUM} text-pstax-gray`}>=</p>
            <div>
              <p className={LABEL}>{c.sumOpen}</p>
              <p className={NUM}>{euro(shownOpen)}</p>
            </div>
          </div>
          {(s.atMin || s.atMax) && (
            <p className={`${LABEL} mt-1`}>
              {c.svsYear} {s.atMin ? c.minNote : `= ${c.maxNote}`}
            </p>
          )}

          <div className="mt-2 border-t border-pstax-border pt-1.5">
            <p className={LABEL}>{c.resultCaption}</p>
            <p className={`${TITLE} ${s.level === 'relevant' ? '!text-emerald-700' : ''}`}>{result.title}</p>
            <p className="mt-1 text-[15px] leading-relaxed text-pstax-navy/85">{result.text}</p>
          </div>
          <p className="sr-only" aria-live="polite" aria-atomic="true">
            {announced}
          </p>
        </div>

        <Expandable
          label={c.whenButton}
          className="mt-3 rounded-xl border border-pstax-border px-3"
        >
          <WhenBody lang={lang} />
        </Expandable>
      </section>

      <p className="mt-3 leading-relaxed">
        <Link to={g.kurzinfoHref} className="font-semibold text-pstax-link hover:underline">
          {g.kurzinfoLink}
        </Link>
      </p>
    </div>
  );
}
